import React, { useEffect, useCallback, useMemo, useState, useRef } from "react";
import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  PrecisionManufacturing as MachineIcon,
  PersonOutline as PersonIcon,
  ScheduleOutlined as ScheduleIcon,
  AddRounded,
} from "@mui/icons-material";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import { useNavigate, useSearchParams } from "react-router-dom";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  fetchWorkOrders,
  fetchWorkOrderFilters,
  fetchWorkOrderSummary,
  fetchKpiSummary,
  fetchTeams,
  changeWorkOrderStatus,
  updateWorkOrder,
  optimisticStatusChange,
  revertStatusChange,
  optimisticAddAssignee,
  revertAddAssignee,
  optimisticSetTeam,
  revertSetTeam,
  optimisticSetMachine,
  revertSetMachine,
  optimisticSetLocation,
  revertSetLocation,
  clearAssignees,
  addAssignees,
  assignTeam,
  removeAssignee,
  generateRecurring,
  reorderWorkOrders,
  optimisticReorder,
  optimisticCrossColumnMove,
  incrementMemberWorkload,
  decrementMemberWorkload,
} from "../../store/workOrdersSlice";
import { dt } from "../../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../../shared/hooks/useRedesignSetup";
import useDataRefresh from "../../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../../shared/hooks/useVisibilityRefresh";
import useChangeHighlight from "../../../../shared/hooks/useChangeHighlight";
import usePermission from "../../../../shared/hooks/usePermission";
import { Modules } from "../../../../core/models/PermissionModels";
import accountContext from "../../../../account/accountContext";
import { Roles } from "../../../../account/models/AccountModels";
import { WorkOrder, WorkOrderStatusCode, WorkOrderTypeCode, ChangeWorkOrderStatus as ChangeStatusModel, Location } from "../../models/models";
import { isValidTransition, columnTargetStatus } from "../../utils/statusTransitions";
import { RootState } from "../../../../core/store";
import { priorityColors } from "../WorkOrderPriorityBadge";
import WorkOrderTypeBadge from "../WorkOrderTypeBadge";
import AssigneeAvatarStack from "../shared/AssigneeAvatarStack";
import BoardColumn from "./BoardColumn";
import KpiStatsBar from "./KpiStatsBar";
import BoardFilterBar from "./BoardFilterBar";
import CompletionDialog from "./CompletionDialog";
import ReworkDialog from "./ReworkDialog";
import TeamManagementDialog from "../teams/TeamManagementDialog";
import TeamSidebar from "../sidebar/TeamSidebar";
import MachineSidebar from "../sidebar/MachineSidebar";
import LocationsSidebarSection from "../sidebar/LocationsSidebarSection";
import TabletTeamsBar from "../sidebar/TabletTeamsBar";
import { getMachines } from "../../../machinery/customerMachinerySlice";
import { CustomerMachineryState } from "../../../machinery/models/models";
import dayjs from "dayjs";
import CreateWorkOrderPanel from "./CreateWorkOrderPanel";
import BoardCalendarView from "./BoardCalendarView";
import LocationConflictDialog from "./LocationConflictDialog";
import { useToastStack, ToastStack } from "../../../../shared/components/ActionToast";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

const SIDEBAR_COLLAPSED_KEY = "wo-board-sidebar-collapsed";

// ─── Column configuration (updated per spec) ────────────────
interface ColumnConfig {
  key: string;
  titleKey: string;
  statusCodes: number[];
  color: string;
  tintColor: string;
}

const COLUMNS: ColumnConfig[] = [
  {
    key: "backlog",
    titleKey: "board.backlog",
    statusCodes: [WorkOrderStatusCode.Created, WorkOrderStatusCode.Assigned, WorkOrderStatusCode.Scheduled],
    color: "#8C9099",      // spec: status-backlog (includes Scheduled)
    tintColor: "transparent",
  },
  {
    key: "inProgress",
    titleKey: "board.inProgress",
    statusCodes: [WorkOrderStatusCode.InProgress],
    color: "#D4572B",      // spec: status-in-progress
    tintColor: "transparent",
  },
  {
    key: "onHold",
    titleKey: "board.onHold",
    statusCodes: [WorkOrderStatusCode.OnHold],
    color: "#E8A817",      // spec: status-on-hold
    tintColor: "transparent",
  },
  {
    key: "pendingReview",
    titleKey: "board.pendingReview",
    statusCodes: [WorkOrderStatusCode.PendingReview],
    color: "#7C3AED",      // spec: status-pending-review (purple)
    tintColor: "transparent",
  },
  {
    key: "completed",
    titleKey: "board.completed",
    statusCodes: [WorkOrderStatusCode.Completed],
    color: "#2D8C4E",      // spec: status-done
    tintColor: "transparent",
  },
];

// Map status code -> translation key for toast messages
const statusNameKeys: Record<number, string> = {
  [WorkOrderStatusCode.Created]: "statuses.created",
  [WorkOrderStatusCode.Assigned]: "statuses.assigned",
  [WorkOrderStatusCode.Scheduled]: "statuses.scheduled",
  [WorkOrderStatusCode.InProgress]: "statuses.inProgress",
  [WorkOrderStatusCode.OnHold]: "statuses.onHold",
  [WorkOrderStatusCode.Completed]: "statuses.completed",
  [WorkOrderStatusCode.Verified]: "statuses.verified",
  [WorkOrderStatusCode.Cancelled]: "statuses.cancelled",
  [WorkOrderStatusCode.PendingReview]: "statuses.pendingReview",
};

interface UndoState {
  workOrderId: string;
  oldStatusId: number;
  newStatusName: string;
}

interface PendingCompleteState {
  workOrderId: string;
  workOrderNumber: string;
  hasMachine: boolean;
  hasLinkedSRs: boolean;
  machineName?: string;
  oldStatusId: number;
}

interface BacklogConfirmState {
  workOrderId: string;
  workOrderNumber: string;
  oldStatusId: number;
}

interface PendingReworkState {
  workOrderId: string;
  workOrderNumber: string;
  oldStatusId: number;
}

interface LocationConflictState {
  workOrderId: string;
  machineId: string;
  machineName: string;
  currentLocation: string;
  currentLocationId?: string;
  machineLocation: string;
  machineLocationId: string;
  machineLocationName?: string;
  machineParentLocationName?: string;
  oldMachineId?: string;
  oldMachineName?: string;
  wo: any; // The full WO object for the updateWorkOrder call
}

const WorkBoardPage = () => {
  useRedesignSetup();
  const { translate } = useTranslation("operations");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canWrite } = usePermission(Modules.Maintenance);
  const currentUserId = accountContext.user?.id;
  const canManage = accountContext.user?.role !== Roles.WORKER;
  const { isPhone, isTablet, isDesktop } = useBreakpoint();

  // ─── Create panel state ─────────────────────────────────
  const [createPanelOpen, setCreatePanelOpen] = useState(false);
  const createPanelPrefill = useMemo(() => {
    const srId = searchParams.get("serviceReportId");
    const mId = searchParams.get("machineId");
    const t = searchParams.get("title");
    if (!srId && !mId && !t) return undefined;
    return {
      serviceReportId: srId || undefined,
      machineId: mId || undefined,
      title: t || undefined,
    };
  }, [searchParams]);

  // Open panel if URL has ?newWo=true
  useEffect(() => {
    if (searchParams.get("newWo") === "true") {
      setCreatePanelOpen(true);
      // Clean up the URL param so it doesn't reopen on refresh
      searchParams.delete("newWo");
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { workOrders, loading, summary, kpiSummary, kpiLoading, teams } = useAppSelector(
    (state: RootState) => state.workOrders
  );

  // Build a userId->userName lookup from teams for sidebar drag-to-assign
  const userNameLookup = useMemo(() => {
    const map: Record<string, string> = {};
    teams.forEach((team) => {
      if (team.members) {
        team.members.forEach((m) => {
          map[m.userId] = m.userName;
        });
      }
    });
    return map;
  }, [teams]);

  // Build a teamId->teamName lookup for sidebar drag
  const teamNameLookup = useMemo(() => {
    const map: Record<string, string> = {};
    teams.forEach((team) => {
      map[team.id] = team.name;
    });
    return map;
  }, [teams]);

  // Machine data for sidebar and DnD
  const { machines: machinesByType } = useAppSelector<CustomerMachineryState>(
    (state) => (state as any).customerMachinery
  );
  const allMachines = useMemo(() => Object.values(machinesByType).flat(), [machinesByType]);
  const machineNameLookup = useMemo(() => {
    const map: Record<string, string> = {};
    allMachines.forEach((m) => {
      map[m.id] = `${m.model}${m.serialNumber ? ` (${m.serialNumber})` : ""}`;
    });
    return map;
  }, [allMachines]);

  // Location data for sidebar DnD — lifted from LocationsSidebarSection via callback
  const [sidebarLocations, setSidebarLocations] = useState<Location[]>([]);
  const handleLocationsLoaded = useCallback((locs: Location[]) => {
    setSidebarLocations(locs);
  }, []);

  // Build locationId -> { name, parentName } lookup for drag handler
  const locationLookup = useMemo(() => {
    const map: Record<string, { name: string; parentName?: string }> = {};
    sidebarLocations.forEach((loc) => {
      map[loc.id] = { name: loc.name, parentName: loc.parentLocationName };
    });
    return map;
  }, [sidebarLocations]);

  // Build machineId -> { locationId, locationName, parentLocationName } for auto-inherit
  const machineLocationLookup = useMemo(() => {
    const map: Record<string, { locationId: string; locationName?: string; parentLocationName?: string }> = {};
    allMachines.forEach((m) => {
      if (m.locationId) {
        map[m.id] = {
          locationId: m.locationId,
          locationName: m.locationName,
          parentLocationName: m.parentLocationName,
        };
      }
    });
    return map;
  }, [allMachines]);

  // Read initial view mode from URL param (?view=board|calendar|list)
  const [viewMode, setViewModeRaw] = useState<"board" | "calendar" | "list">(() => {
    const viewParam = searchParams.get("view");
    if (viewParam === "calendar" || viewParam === "list") return viewParam;
    return "board";
  });

  // Update URL param when view mode changes
  const setViewMode = useCallback((mode: "board" | "calendar" | "list") => {
    setViewModeRaw(mode);
    const next = new URLSearchParams(searchParams);
    if (mode === "board") {
      next.delete("view");
    } else {
      next.set("view", mode);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const [showCancelled, setShowCancelled] = useState(false);
  const [activePhoneTab, setActivePhoneTab] = useState(0);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  // ─── Narrow desktop detection (1280-1440px) ────────────────
  const [isNarrowDesktop, setIsNarrowDesktop] = useState(() => {
    if (typeof window === "undefined") return false;
    const w = window.innerWidth;
    return w >= 1280 && w < 1440;
  });

  // ─── Wide tablet detection (1100-1279px: stats + filters fit on same row) ──
  const [isWideTablet, setIsWideTablet] = useState(() => {
    if (typeof window === "undefined") return false;
    const w = window.innerWidth;
    return w >= 1100 && w < 1280;
  });

  // Track narrow desktop + wide tablet on resize
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsNarrowDesktop(w >= 1280 && w < 1440);
      setIsWideTablet(w >= 1100 && w < 1280);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-collapse sidebar at narrow desktop on mount if no explicit user preference
  useEffect(() => {
    if (!isDesktop) return;
    const w = window.innerWidth;
    if (w >= 1280 && w < 1440) {
      try {
        const explicit = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        if (explicit === null) {
          // No explicit preference — auto-collapse for narrow desktop
          setSidebarCollapsed(true);
        }
      } catch { /* ignore */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Machine section collapse state (persistent via localStorage)
  const MACHINES_SECTION_COLLAPSED_KEY = "wo-board-machines-collapsed";
  const [machinesSectionCollapsed, setMachinesSectionCollapsed] = useState(() => {
    try { return localStorage.getItem(MACHINES_SECTION_COLLAPSED_KEY) === "true"; } catch { return false; }
  });
  const handleToggleMachinesSection = useCallback(() => {
    setMachinesSectionCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(MACHINES_SECTION_COLLAPSED_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Teams section collapse state (persistent via localStorage)
  const TEAMS_SECTION_COLLAPSED_KEY = "wo-board-teams-collapsed";
  const [teamsSectionCollapsed, setTeamsSectionCollapsed] = useState(() => {
    try { return localStorage.getItem(TEAMS_SECTION_COLLAPSED_KEY) === "true"; } catch { return false; }
  });
  const handleToggleTeamsSection = useCallback(() => {
    setTeamsSectionCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(TEAMS_SECTION_COLLAPSED_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Locations section collapse state (persistent via localStorage)
  const LOCATIONS_SECTION_COLLAPSED_KEY = "wo-board-locations-collapsed";
  const [locationsSectionCollapsed, setLocationsSectionCollapsed] = useState(() => {
    try { return localStorage.getItem(LOCATIONS_SECTION_COLLAPSED_KEY) === "true"; } catch { return false; }
  });
  const handleToggleLocationsSection = useCallback(() => {
    setLocationsSectionCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(LOCATIONS_SECTION_COLLAPSED_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Team management dialog
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);

  // Calendar fullscreen state (hides sidebar + pill rail)
  const [calendarFullscreen, setCalendarFullscreen] = useState(false);

  // Listen for custom event from CalendarBacklogPanel's "+ New" button
  useEffect(() => {
    const handler = () => setCreatePanelOpen(true);
    window.addEventListener("openCreatePanel", handler);
    return () => window.removeEventListener("openCreatePanel", handler);
  }, []);

  const handleToggleSidebarCollapse = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch { /* ignore */ }
      return next;
    });
  }, []);

  // DnD state
  const [pendingComplete, setPendingComplete] = useState<PendingCompleteState | null>(null);
  const [backlogConfirm, setBacklogConfirm] = useState<BacklogConfirmState | null>(null);
  const [pendingRework, setPendingRework] = useState<PendingReworkState | null>(null);
  const [locationConflict, setLocationConflict] = useState<LocationConflictState | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { addToast, dismissToast, toasts } = useToastStack();
  const undoToastIdRef = useRef<number | null>(null);
  const undoStateRef = useRef<UndoState | null>(null);

  const isDragEnabled = canWrite && !isPhone;

  // ─── Data fetching ─────────────────────────────────────────
  const loadData = useCallback(() => {
    dispatch(generateRecurring());

    const statusIds = showCancelled
      ? [1, 2, 3, 4, 5, 6, 7, 8, 9]
      : [1, 2, 3, 4, 5, 6, 7, 9];
    dispatch(fetchWorkOrders({
      pageNumber: 1,
      pageSize: 500,
      statusIds,
    }));
    dispatch(fetchWorkOrderFilters());
    dispatch(fetchWorkOrderSummary());
    dispatch(fetchKpiSummary());
    dispatch(fetchTeams()); // Refresh sidebar workload counts on every data reload
    dispatch(getMachines({})); // Refresh machine sidebar data
  }, [dispatch, showCancelled]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useDataRefresh("workOrders", loadData);
  useVisibilityRefresh(loadData);

  const changedIds = useChangeHighlight(
    workOrders,
    wo => wo.id,
    wo => `${wo.workOrderStatusId}-${wo.modifiedDate}`
  );

  // ─── Client-side filter logic ──────────────────────────────
  const filteredWorkOrders = useMemo(() => {
    let result = workOrders;

    // Apply active filter
    if (activeFilter === "critical") {
      result = result.filter(wo =>
        wo.priorityCode === 1 &&
        [1, 2, 3, 4, 5, 9].includes(wo.workOrderStatusId)
      );
    } else if (activeFilter === "overdue") {
      result = result.filter(wo =>
        wo.dueDate &&
        dayjs(wo.dueDate).isBefore(dayjs()) &&
        [1, 2, 3, 4, 5, 9].includes(wo.workOrderStatusId)
      );
    } else if (activeFilter === "myTasks") {
      result = result.filter(wo => wo.assignedToUserId === currentUserId);
    } else if (activeFilter === "preventive") {
      result = result.filter(wo => wo.workOrderTypeId === WorkOrderTypeCode.PreventiveMaintenance);
    }

    // Apply search (debounced from BoardFilterBar)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(wo =>
        wo.number?.toLowerCase().includes(term) ||
        wo.title?.toLowerCase().includes(term) ||
        wo.machineName?.toLowerCase().includes(term) ||
        wo.assignedToUserName?.toLowerCase().includes(term)
      );
    }

    return result;
  }, [workOrders, activeFilter, searchTerm, currentUserId]);

  // ─── Filter counts (computed from ALL workOrders, not filtered) ──
  const filterCounts = useMemo(() => {
    const nonCancelled = workOrders.filter(wo => wo.workOrderStatusId !== WorkOrderStatusCode.Cancelled);
    return {
      all: nonCancelled.length,
      critical: workOrders.filter(wo =>
        wo.priorityCode === 1 && [1, 2, 3, 4, 5, 9].includes(wo.workOrderStatusId)
      ).length,
      overdue: workOrders.filter(wo =>
        wo.dueDate &&
        dayjs(wo.dueDate).isBefore(dayjs()) &&
        [1, 2, 3, 4, 5, 9].includes(wo.workOrderStatusId)
      ).length,
      myTasks: workOrders.filter(wo => wo.assignedToUserId === currentUserId).length,
      preventive: workOrders.filter(wo =>
        wo.workOrderTypeId === WorkOrderTypeCode.PreventiveMaintenance
      ).length,
    };
  }, [workOrders, currentUserId]);

  // ─── Group filtered WOs into columns ───────────────────────
  const columnData = useMemo(() => {
    return COLUMNS.map((col) => {
      const items = filteredWorkOrders
        .filter((wo) => col.statusCodes.includes(wo.workOrderStatusId))
        .sort((a, b) => {
          const posA = a.boardPosition ?? Infinity;
          const posB = b.boardPosition ?? Infinity;
          if (posA !== posB) return posA - posB;
          // Fallback: newest first
          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
        });
      // Compute count from summary if available, otherwise items length
      let count = items.length;
      if (summary && !activeFilter && !searchTerm.trim()) {
        count = col.statusCodes.reduce((sum, code) => {
          switch (code) {
            case WorkOrderStatusCode.Created: return sum + summary.created;
            case WorkOrderStatusCode.Assigned: return sum + summary.assigned;
            case WorkOrderStatusCode.Scheduled: return sum + summary.scheduled;
            case WorkOrderStatusCode.InProgress: return sum + summary.inProgress;
            case WorkOrderStatusCode.OnHold: return sum + summary.onHold;
            case WorkOrderStatusCode.Completed: return sum + summary.completed;
            case WorkOrderStatusCode.PendingReview: return sum + summary.pendingReview;
            default: return sum;
          }
        }, 0);
      }
      return { ...col, items, count };
    });
  }, [filteredWorkOrders, summary, activeFilter, searchTerm]);

  const handleCardClick = useCallback(
    (id: string) => navigate(`/operations/work-orders/${id}`),
    [navigate]
  );

  // ─── Machine assignment helper (shared by drag handler + location conflict dialog) ──
  const executeMachineAssignment = useCallback(
    (wo: any, machineId: string, machineName: string, shouldInheritLocation: boolean, machineLocInfo?: { locationId: string; locationName?: string; parentLocationName?: string }, overrideLocationId?: string) => {
      const workOrderId = wo.id;
      const oldMachineId = wo.machineId;
      const oldMachineName = wo.machineName;
      const oldLocationId = wo.locationId;
      const oldLocationName = wo.locationName;
      const oldParentLocationName = wo.parentLocationName;

      // Determine which locationId to send
      const finalLocationId = overrideLocationId !== undefined
        ? overrideLocationId
        : shouldInheritLocation && machineLocInfo
          ? machineLocInfo.locationId
          : wo.locationId;

      // Optimistic update — machine
      dispatch(optimisticSetMachine({ workOrderId, machineId, machineName }));
      // Optimistic update — location (auto-inherit or override)
      if (shouldInheritLocation && machineLocInfo) {
        dispatch(optimisticSetLocation({
          workOrderId,
          locationId: machineLocInfo.locationId,
          locationName: machineLocInfo.locationName || "Location",
          parentLocationName: machineLocInfo.parentLocationName,
        }));
      } else if (overrideLocationId && machineLocInfo) {
        dispatch(optimisticSetLocation({
          workOrderId,
          locationId: machineLocInfo.locationId,
          locationName: machineLocInfo.locationName || "Location",
          parentLocationName: machineLocInfo.parentLocationName,
        }));
      }

      dispatch(
        updateWorkOrder({
          id: workOrderId,
          model: {
            title: wo.title,
            workOrderTypeId: wo.workOrderTypeId,
            priorityCode: wo.priorityCode,
            machineId,
            locationId: finalLocationId,
            description: wo.description,
            assignedToUserId: wo.assignedToUserId,
            assignedToUserName: wo.assignedToUserName,
            dueDate: wo.dueDate,
            scheduledStartDate: wo.scheduledStartDate,
            estimatedDurationMinutes: wo.estimatedDurationMinutes,
            serviceReportId: wo.serviceReportId,
            parentWorkOrderId: wo.parentWorkOrderId,
            teamId: wo.teamId,
          },
        })
      )
        .unwrap()
        .then((result: any) => {
          // Verify the machine was actually assigned by checking the returned DTO
          if (result && result.machineId !== machineId) {
            dispatch(revertSetMachine({ workOrderId, oldMachineId, oldMachineName }));
            if (shouldInheritLocation || overrideLocationId) {
              dispatch(revertSetLocation({ workOrderId, oldLocationId, oldLocationName, oldParentLocationName }));
            }
            addToast("error", translate("machineSidebar.assignFailed"), { duration: 4000 });
            return;
          }
          addToast("success", translate("machineSidebar.machineAssigned", { machine: machineName, wo: wo.number }), { duration: 3000 });
          loadData();
        })
        .catch(() => {
          dispatch(revertSetMachine({ workOrderId, oldMachineId, oldMachineName }));
          if (shouldInheritLocation || overrideLocationId) {
            dispatch(revertSetLocation({ workOrderId, oldLocationId, oldLocationName, oldParentLocationName }));
          }
          addToast("error", translate("machineSidebar.assignFailed"), { duration: 4000 });
        });
    },
    [dispatch, translate, loadData]
  );

  // ─── Location conflict dialog handlers ──
  const handleLocationConflictKeepCurrent = useCallback(() => {
    if (!locationConflict) return;
    const { wo, machineId, machineName } = locationConflict;
    setLocationConflict(null);
    // Assign machine but keep WO's current location
    executeMachineAssignment(wo, machineId, machineName, false, undefined, undefined);
  }, [locationConflict, executeMachineAssignment]);

  const handleLocationConflictUpdateLocation = useCallback(() => {
    if (!locationConflict) return;
    const { wo, machineId, machineName, machineLocationId, machineLocationName, machineParentLocationName } = locationConflict;
    setLocationConflict(null);
    // Assign machine AND update WO location to machine's location
    const machineLocInfo = {
      locationId: machineLocationId,
      locationName: machineLocationName,
      parentLocationName: machineParentLocationName,
    };
    executeMachineAssignment(wo, machineId, machineName, false, machineLocInfo, machineLocationId);
  }, [locationConflict, executeMachineAssignment]);

  // ─── DnD: onDragEnd handler (multi-type: CARD, PERSON, TEAM) ──
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source, draggableId } = result;

      if (!destination) return;

      // ─── 1. WO card dragged (same-column reorder or cross-column status change) ───
      if (draggableId.startsWith("wo-")) {
        const workOrderId = draggableId.replace("wo-", "");
        const sourceColumnKey = source.droppableId;
        const targetColumnKey = destination.droppableId;
        const isSameColumn = sourceColumnKey === targetColumnKey;

        // Dropped in exact same position — no-op
        if (isSameColumn && destination.index === source.index) {
          return;
        }

        // ─── Same-column reorder ───
        if (isSameColumn) {
          const col = columnData.find(c => c.key === sourceColumnKey);
          if (!col) return;

          // Build the new ordered list of IDs for this column
          const orderedItems = [...col.items];
          const [moved] = orderedItems.splice(source.index, 1);
          orderedItems.splice(destination.index, 0, moved);
          const orderedIds = orderedItems.map(w => w.id);

          // Optimistic local reorder
          dispatch(optimisticReorder({ orderedIds }));

          // Determine the target status for the reorder API — use the first status code for this column
          const reorderStatusId = columnTargetStatus[targetColumnKey];
          if (reorderStatusId !== undefined) {
            // For columns with multiple statuses (e.g. backlog: Created+Assigned),
            // send reorder per status group within the column
            const statusGroups: Record<number, string[]> = {};
            orderedItems.forEach(item => {
              if (!statusGroups[item.workOrderStatusId]) {
                statusGroups[item.workOrderStatusId] = [];
              }
              statusGroups[item.workOrderStatusId].push(item.id);
            });
            Object.keys(statusGroups).forEach(key => {
              const statusId = Number(key);
              dispatch(reorderWorkOrders({ statusId, workOrderIds: statusGroups[statusId] }))
                .unwrap()
                .catch(() => loadData()); // Restore server truth on failure
            });
          }
          return;
        }

        // ─── Cross-column drop → status change ───
        const targetStatusId = columnTargetStatus[targetColumnKey];
        if (targetStatusId === undefined) return;

        const wo = workOrders.find(w => w.id === workOrderId);
        if (!wo) return;

        const currentStatusId = wo.workOrderStatusId;
        if (currentStatusId === targetStatusId) return;

        // Backlog confirmation: if dragging back to Backlog from another column
        if (targetColumnKey === "backlog" && currentStatusId !== WorkOrderStatusCode.Created) {
          setBacklogConfirm({
            workOrderId: wo.id,
            workOrderNumber: wo.number,
            oldStatusId: currentStatusId,
          });
          return;
        }

        if (!isValidTransition(currentStatusId, targetStatusId)) {
          const fromName = translate(statusNameKeys[currentStatusId] || "statuses.created");
          const toName = translate(statusNameKeys[targetStatusId] || "statuses.created");
          addToast("error", translate("board.invalidTransition", { from: fromName, to: toName }), { duration: 4000 });
          return;
        }

        if (targetStatusId === WorkOrderStatusCode.PendingReview) {
          setPendingComplete({
            workOrderId: wo.id,
            workOrderNumber: wo.number,
            hasMachine: !!wo.machineId,
            hasLinkedSRs: !!(wo.linkedServiceReports && wo.linkedServiceReports.length > 0),
            machineName: wo.machineName,
            oldStatusId: currentStatusId,
          });
          return;
        }

        // Rework: dragging FROM PendingReview BACK to InProgress → show ReworkDialog
        if (currentStatusId === WorkOrderStatusCode.PendingReview && targetStatusId === WorkOrderStatusCode.InProgress) {
          setPendingRework({
            workOrderId: wo.id,
            workOrderNumber: wo.number,
            oldStatusId: currentStatusId,
          });
          return;
        }

        // Optimistically move the card to the correct position in the target column
        const targetCol = COLUMNS.find(c => c.key === targetColumnKey);
        if (targetCol) {
          dispatch(optimisticCrossColumnMove({
            workOrderId: wo.id,
            newStatusId: targetStatusId,
            targetColumnStatusCodes: targetCol.statusCodes,
            destinationIndex: destination.index,
          }));
        }

        // Fire the status change API (skip reload — optimistic state is correct)
        performStatusChange(wo.id, currentStatusId, targetStatusId, undefined, undefined, undefined, true);

        // Send the new column order for the destination column
        const destCol = columnData.find(c => c.key === targetColumnKey);
        if (destCol) {
          const destItems = [...destCol.items];
          destItems.splice(destination.index, 0, wo);
          const destIds = destItems.map(w => w.id);
          // Brief delay to allow status change to propagate on the server
          setTimeout(() => {
            dispatch(reorderWorkOrders({ statusId: targetStatusId, workOrderIds: destIds }));
          }, 500);
        }
        return;
      }

      // ─── 2. Person dragged onto a card → add assignee ──────────
      if (draggableId.startsWith("person::")) {
        if (!destination.droppableId.startsWith("card-")) return;

        // Format: person::{teamId}::{userId}  — split on "::" to avoid GUID dash ambiguity
        const personParts = draggableId.split("::");
        const userId = personParts[2]; // [0]="person", [1]=teamId, [2]=userId
        const workOrderId = destination.droppableId.replace("card-", "");
        const userName = userNameLookup[userId] || "User";

        const wo = workOrders.find(w => w.id === workOrderId);
        if (!wo) return;

        // Check if already assigned
        const alreadyAssigned = wo.assignees?.some(a => a.userId === userId);
        if (alreadyAssigned) {
          addToast("error", translate("sidebar.alreadyAssigned"), { duration: 4000 });
          return;
        }

        // Optimistic update — card avatar + sidebar workload count
        dispatch(optimisticAddAssignee({ workOrderId, userId, userName }));
        dispatch(incrementMemberWorkload({ userId }));

        // Fire API call
        dispatch(addAssignees({ workOrderId, request: { assignees: [{ userId }] } }))
          .unwrap()
          .then(() => {
            addToast("success", translate("sidebar.personAssigned", { name: userName, wo: wo.number }), { duration: 3000 });
            loadData(); // Refresh to get server-side truth
            dispatch(fetchTeams()); // Refresh sidebar workload counts
          })
          .catch(() => {
            dispatch(revertAddAssignee({ workOrderId, userId }));
            dispatch(decrementMemberWorkload({ userId }));
            addToast("error", translate("sidebar.assignFailed"), { duration: 4000 });
          });
        return;
      }

      // ─── 3. Team dragged onto a card → assign all members individually + set team scope ──────────
      if (draggableId.startsWith("team-")) {
        if (!destination.droppableId.startsWith("card-")) return;

        const teamId = draggableId.replace("team-", "");
        const workOrderId = destination.droppableId.replace("card-", "");
        const teamName = teamNameLookup[teamId] || "Team";

        const wo = workOrders.find(w => w.id === workOrderId);
        if (!wo) return;

        // Find the team in Redux state to get its members
        const team = teams.find(t => t.id === teamId);
        const teamMembers = team?.members || [];

        // Filter out members who are already assigned to this WO
        const existingAssigneeIds = new Set((wo.assignees || []).map(a => a.userId));
        const newMembers = teamMembers.filter(m => !existingAssigneeIds.has(m.userId));

        if (newMembers.length === 0 && wo.teamId === teamId) {
          // All members already assigned and team already set — no-op
          return;
        }

        // Optimistic update: add all new member avatars to the card + sidebar workload counts
        newMembers.forEach(member => {
          dispatch(optimisticAddAssignee({
            workOrderId,
            userId: member.userId,
            userName: member.userName,
          }));
          dispatch(incrementMemberWorkload({ userId: member.userId }));
        });

        // Also set TeamId for the scope label
        const oldTeamId = wo.teamId;
        if (wo.teamId !== teamId) {
          dispatch(optimisticSetTeam({ workOrderId, teamId }));
        }

        // Fire API calls sequentially — assignees first (sets AssignedToUserId via
        // SyncLeadCache), then team (reads the now-correct entity). Parallel calls
        // caused a race condition where assignTeam's full-entity save overwrote
        // the AssignedToUserId set by addAssignees.
        const runAssignAndTeam = async () => {
          if (newMembers.length > 0) {
            await dispatch(addAssignees({
              workOrderId,
              request: { assignees: newMembers.map(m => ({ userId: m.userId })) },
            })).unwrap();
          }
          if (wo.teamId !== teamId) {
            await dispatch(assignTeam({ workOrderId, request: { teamId } })).unwrap();
          }
        };

        runAssignAndTeam()
          .then(() => {
            addToast("success", translate("sidebar.teamAssignedBoard", { team: teamName, wo: wo.number }), { duration: 3000 });
            loadData(); // Refresh to get server-side truth
            dispatch(fetchTeams()); // Refresh sidebar workload counts
          })
          .catch(() => {
            // Revert optimistic updates on failure
            newMembers.forEach(member => {
              dispatch(revertAddAssignee({ workOrderId, userId: member.userId }));
              dispatch(decrementMemberWorkload({ userId: member.userId }));
            });
            if (wo.teamId !== teamId) {
              dispatch(revertSetTeam({ workOrderId, oldTeamId }));
            }
            addToast("error", translate("sidebar.assignFailed"), { duration: 4000 });
          });
        return;
      }

      // ─── 4. Machine dragged onto a card → assign machine (+ location conflict detection) ──────────
      if (draggableId.startsWith("machine::")) {
        if (!destination.droppableId.startsWith("card-")) return;

        const machineId = draggableId.split("::")[1];
        const workOrderId = destination.droppableId.replace("card-", "");
        const machineName = machineNameLookup[machineId] || translate("machineSidebar.unknown");

        const wo = workOrders.find((w) => w.id === workOrderId);
        if (!wo) return;

        // Already assigned to same machine — no-op
        if (wo.machineId === machineId) {
          addToast("error", translate("machineSidebar.alreadyAssigned"), { duration: 4000 });
          return;
        }

        const machineLocInfo = machineLocationLookup[machineId];

        // Location conflict detection:
        // 1. WO has no location + machine has one → auto-inherit silently
        // 2. WO has same location as machine → no conflict, proceed
        // 3. WO has DIFFERENT location than machine → show conflict dialog
        const hasLocationConflict =
          !!wo.locationId && !!machineLocInfo && wo.locationId !== machineLocInfo.locationId;

        if (hasLocationConflict) {
          // Build display names for locations
          const woLocationDisplay = wo.parentLocationName
            ? `${wo.locationName}, ${wo.parentLocationName}`
            : wo.locationName || "Unknown";
          const machineLocationDisplay = machineLocInfo.parentLocationName
            ? `${machineLocInfo.locationName || "Location"}, ${machineLocInfo.parentLocationName}`
            : machineLocInfo.locationName || "Location";

          setLocationConflict({
            workOrderId,
            machineId,
            machineName,
            currentLocation: woLocationDisplay,
            currentLocationId: wo.locationId,
            machineLocation: machineLocationDisplay,
            machineLocationId: machineLocInfo.locationId,
            machineLocationName: machineLocInfo.locationName,
            machineParentLocationName: machineLocInfo.parentLocationName,
            oldMachineId: wo.machineId,
            oldMachineName: wo.machineName,
            wo,
          });
          return;
        }

        // No conflict — proceed with assignment
        const shouldInheritLocation = !wo.locationId && !!machineLocInfo;
        executeMachineAssignment(wo, machineId, machineName, shouldInheritLocation, machineLocInfo);
        return;
      }

      // ─── 5. Location dragged onto a card → assign location ──────────
      if (draggableId.startsWith("location::")) {
        if (!destination.droppableId.startsWith("card-")) return;

        const locationId = draggableId.split("::")[1];
        const workOrderId = destination.droppableId.replace("card-", "");
        const locInfo = locationLookup[locationId];
        const locationName = locInfo?.name || "Location";
        const parentLocationName = locInfo?.parentName;

        const wo = workOrders.find((w) => w.id === workOrderId);
        if (!wo) return;

        // Already assigned to same location — no-op
        if (wo.locationId === locationId) {
          addToast("error", translate("locationSidebar.alreadyAssigned"), { duration: 4000 });
          return;
        }

        const oldLocationId = wo.locationId;
        const oldLocationName = wo.locationName;
        const oldParentLocationName = wo.parentLocationName;

        // Optimistic update
        dispatch(optimisticSetLocation({ workOrderId, locationId, locationName, parentLocationName }));

        // Fire API — use updateWorkOrder PUT with all existing fields preserved
        dispatch(
          updateWorkOrder({
            id: workOrderId,
            model: {
              title: wo.title,
              workOrderTypeId: wo.workOrderTypeId,
              priorityCode: wo.priorityCode,
              machineId: wo.machineId,
              locationId,
              description: wo.description,
              assignedToUserId: wo.assignedToUserId,
              assignedToUserName: wo.assignedToUserName,
              dueDate: wo.dueDate,
              scheduledStartDate: wo.scheduledStartDate,
              estimatedDurationMinutes: wo.estimatedDurationMinutes,
              serviceReportId: wo.serviceReportId,
              parentWorkOrderId: wo.parentWorkOrderId,
              teamId: wo.teamId,
            },
          })
        )
          .unwrap()
          .then(() => {
            const displayName = parentLocationName
              ? `${parentLocationName} > ${locationName}`
              : locationName;
            addToast("success", translate("locationSidebar.locationAssigned", { location: displayName, wo: wo.number }), { duration: 3000 });
            loadData();
          })
          .catch(() => {
            dispatch(revertSetLocation({ workOrderId, oldLocationId, oldLocationName, oldParentLocationName }));
            addToast("error", translate("locationSidebar.assignFailed"), { duration: 4000 });
          });
        return;
      }
    },
    [workOrders, columnData, translate, dispatch, userNameLookup, teamNameLookup, teams, loadData, machineNameLookup, locationLookup, machineLocationLookup, executeMachineAssignment]
  );

  // ─── Perform status change with optimistic update ──────────
  const performStatusChange = useCallback(
    async (
      workOrderId: string,
      oldStatusId: number,
      newStatusId: number,
      completionNotes?: string,
      cost?: number,
      createServiceReport?: boolean,
      skipReload?: boolean,
      notes?: string
    ) => {
      // Only apply optimisticStatusChange when NOT called from cross-column move
      // (cross-column moves use optimisticCrossColumnMove which already sets the status)
      if (!skipReload) {
        dispatch(optimisticStatusChange({ id: workOrderId, newStatusId }));
      }

      const model: ChangeStatusModel = {
        newStatusId,
        notes: notes || undefined,
        completionNotes: newStatusId === WorkOrderStatusCode.PendingReview ? completionNotes || undefined : undefined,
        cost: newStatusId === WorkOrderStatusCode.PendingReview && cost != null ? cost : undefined,
        createServiceReport: newStatusId === WorkOrderStatusCode.PendingReview ? !!createServiceReport : false,
      };

      try {
        await dispatch(changeWorkOrderStatus({ id: workOrderId, model })).unwrap();

        const newStatusName = translate(statusNameKeys[newStatusId] || "statuses.created");
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        undoStateRef.current = { workOrderId, oldStatusId, newStatusName };
        // Dismiss previous undo toast if still visible
        if (undoToastIdRef.current != null) {
          dismissToast(undoToastIdRef.current);
        }
        undoToastIdRef.current = addToast("undo", translate("board.statusChanged", { status: newStatusName }), {
          duration: 5000,
          action: (
            <Box
              onClick={() => handleUndoRef.current()}
              sx={{
                fontFamily: dt.font,
                fontWeight: 700,
                fontSize: "12px",
                color: "#3B82F6",
                cursor: "pointer",
                textTransform: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {translate("board.undo")}
            </Box>
          ),
        });
        undoTimerRef.current = setTimeout(() => { undoStateRef.current = null; }, 5000);
        // Skip reload for cross-column moves — optimistic state is already correct
        if (!skipReload) {
          loadData();
        }
      } catch {
        dispatch(revertStatusChange({ id: workOrderId, oldStatusId }));
        addToast("error", translate("board.invalidTransition", { from: "", to: "" }), { duration: 4000 });
        // On error, always reload to restore server truth
        loadData();
      }
    },
    [dispatch, translate, loadData]
  );

  // ─── Undo handler (uses ref for fresh state in toast action callbacks) ──────
  const handleUndoRef = useRef<() => void>(() => {});
  handleUndoRef.current = async () => {
    const state = undoStateRef.current;
    if (!state) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);

    const { workOrderId, oldStatusId } = state;
    undoStateRef.current = null;
    // Dismiss the undo toast immediately
    if (undoToastIdRef.current != null) {
      dismissToast(undoToastIdRef.current);
      undoToastIdRef.current = null;
    }
    dispatch(optimisticStatusChange({ id: workOrderId, newStatusId: oldStatusId }));

    const model: ChangeStatusModel = {
      newStatusId: oldStatusId,
      createServiceReport: false,
    };

    try {
      await dispatch(changeWorkOrderStatus({ id: workOrderId, model })).unwrap();
      loadData();
    } catch {
      loadData();
    }
  };

  // ─── Completion overlay handlers ───────────────────────────
  const handleCompletionConfirm = useCallback(
    (completionNotes: string, cost: number | undefined, createSR: boolean) => {
      if (!pendingComplete) return;
      performStatusChange(
        pendingComplete.workOrderId,
        pendingComplete.oldStatusId,
        WorkOrderStatusCode.PendingReview,
        completionNotes,
        cost,
        createSR
      );
      setPendingComplete(null);
    },
    [pendingComplete, performStatusChange]
  );

  const handleCompletionCancel = useCallback(() => {
    setPendingComplete(null);
  }, []);

  // ─── Rework overlay handlers ──────────────────────────────
  const handleReworkConfirm = useCallback(
    (reworkNotes: string) => {
      if (!pendingRework) return;
      performStatusChange(
        pendingRework.workOrderId,
        pendingRework.oldStatusId,
        WorkOrderStatusCode.InProgress,
        undefined,  // completionNotes
        undefined,  // cost
        undefined,  // createServiceReport
        false,      // skipReload
        reworkNotes // notes (rework instructions)
      );
      setPendingRework(null);
    },
    [pendingRework, performStatusChange]
  );

  const handleReworkCancel = useCallback(() => {
    setPendingRework(null);
  }, []);

  // ─── Backlog confirmation handlers ──────────────────────────
  const handleBacklogKeepAssignee = useCallback(() => {
    if (!backlogConfirm) return;
    performStatusChange(
      backlogConfirm.workOrderId,
      backlogConfirm.oldStatusId,
      WorkOrderStatusCode.Created
    );
    setBacklogConfirm(null);
  }, [backlogConfirm, performStatusChange]);

  const handleBacklogRemoveAssignee = useCallback(async () => {
    if (!backlogConfirm) return;
    performStatusChange(
      backlogConfirm.workOrderId,
      backlogConfirm.oldStatusId,
      WorkOrderStatusCode.Created
    );
    // Clear assignees locally and via API
    dispatch(clearAssignees({ workOrderId: backlogConfirm.workOrderId }));
    const wo = workOrders.find(w => w.id === backlogConfirm.workOrderId);
    if (wo?.assignees && wo.assignees.length > 0) {
      // Optimistically decrement sidebar workload counts
      wo.assignees.forEach(a => dispatch(decrementMemberWorkload({ userId: a.userId })));
      // Remove each assignee via API
      for (const assignee of wo.assignees) {
        try {
          await dispatch(removeAssignee({
            workOrderId: backlogConfirm.workOrderId,
            userId: assignee.userId,
          })).unwrap();
        } catch {
          // Swallow — will refresh anyway
        }
      }
    }
    setBacklogConfirm(null);
    loadData();
    dispatch(fetchTeams()); // Refresh sidebar workload counts
  }, [backlogConfirm, performStatusChange, dispatch, workOrders, loadData]);

  const handleBacklogCancel = useCallback(() => {
    setBacklogConfirm(null);
  }, []);

  // ─── Render ────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "#F2F0EB",  /* spec: board-bg warm tone */
        backgroundImage: "none",
        padding: { xs: "12px", sm: "20px", md: "28px" },
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
      }}
    >
      {/* Unified Stats + Filter Bar — white strip with bottom border */}
      {/* Phone: 2 rows — Row 1: stats + CTA, Row 2: filter chips */}
      {/* Narrow tablet (768-1099px): 2 rows — stats above, filters below */}
      {/* Wide tablet (1100-1279px) + desktop: single row */}
      <Box
        sx={{
          display: "flex",
          flexDirection: (isPhone || (isTablet && !isWideTablet)) ? "column" : "row",
          alignItems: (isPhone || (isTablet && !isWideTablet)) ? "stretch" : "center",
          gap: isPhone ? "8px" : (isTablet && !isWideTablet) ? "8px" : "32px",
          backgroundColor: "#FFFFFF",
          padding: isPhone ? "10px 16px" : isTablet ? "12px 18px" : "16px 24px",
          borderBottom: "1px solid #E8E5DF",
          borderRadius: isPhone ? "10px" : "12px",
          marginBottom: isPhone ? "8px" : isTablet ? "12px" : "16px",
          flexShrink: 0,
          animation: "headerReveal 0.5s ease both",
        }}
      >
        {/* Phone Row 1: Stats left + CTA right */}
        {isPhone ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <KpiStatsBar kpiSummary={kpiSummary} loading={kpiLoading} compact />
            {canWrite && (
              <Box
                onClick={() => setCreatePanelOpen(true)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  backgroundColor: "#D4572B",
                  color: "#FFFFFF",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: "11px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  transition: "all 0.15s ease",
                  "&:active": { transform: "scale(0.97)" },
                }}
              >
                <AddRounded sx={{ fontSize: 14 }} />
                {translate("board.createShort")}
              </Box>
            )}
          </Box>
        ) : (
          /* Non-phone: Stats section (left / top row at narrow tablet) */
          <KpiStatsBar kpiSummary={kpiSummary} loading={kpiLoading} compact={isNarrowDesktop || isTablet} />
        )}

        {/* Divider between stats and filters (single-row layouts only) */}
        {!isPhone && (isDesktop || isWideTablet) && (
          <Box sx={{ width: "1px", height: "24px", backgroundColor: "#E8E5DF", flexShrink: 0 }} />
        )}

        {/* Filter + actions section (right / below on phone & narrow tablet) */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <BoardFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            showCancelled={showCancelled}
            onToggleCancelled={setShowCancelled}
            counts={filterCounts}
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={handleToggleSidebarCollapse}
            canManage={canManage}
            canWrite={canWrite}
            onCreateWorkOrder={() => setCreatePanelOpen(true)}
            hideCreateButton={isPhone}
          />
        </Box>
      </Box>

      {/* Content: board or list view — dims when create panel is open */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          opacity: createPanelOpen ? 0.4 : 1,
          pointerEvents: createPanelOpen ? "none" : "auto",
          transition: "opacity 0.3s ease",
        }}
      >
      {viewMode === "calendar" ? (
        /* ─── Calendar View ─── */
        <DragDropContext onDragEnd={handleDragEnd}>
        <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
          {/* Sidebar: same as board mode (desktop only), hidden in fullscreen */}
          {isDesktop && canManage && !calendarFullscreen && (
            <Box
              sx={{
                width: sidebarCollapsed ? "48px" : "260px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                flexShrink: 0,
                transition: "width 0.25s ease",
                backgroundColor: "#FFFFFF",
                borderRight: "1px solid #E8E5DF",
                borderRadius: "12px 0 0 12px",
                paddingTop: "16px",
                paddingBottom: "16px",
                overflowY: "auto",
                overflowX: "hidden",
                scrollbarWidth: "thin",
                scrollbarColor: "#C1C7CD transparent",
                "&::-webkit-scrollbar": { width: "8px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#C1C7CD", borderRadius: "4px" },
                "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A0A8B1" },
              }}
            >
              <TeamSidebar
                collapsed={sidebarCollapsed}
                sectionCollapsed={!sidebarCollapsed && teamsSectionCollapsed}
                onToggleSectionCollapse={handleToggleTeamsSection}
                onToggleCollapse={handleToggleSidebarCollapse}
                onOpenTeamManagement={() => setTeamDialogOpen(true)}
                canManage={canManage}
              />
              {!sidebarCollapsed && (
                <Box sx={{ height: "1px", backgroundColor: "#E8E5DF", margin: "12px 0", flexShrink: 0 }} />
              )}
              <MachineSidebar
                collapsed={sidebarCollapsed}
                sectionCollapsed={!sidebarCollapsed && machinesSectionCollapsed}
                onToggleSectionCollapse={handleToggleMachinesSection}
              />
              {!sidebarCollapsed && (
                <Box sx={{ height: "1px", backgroundColor: "#E8E5DF", margin: "12px 0", flexShrink: 0 }} />
              )}
              <LocationsSidebarSection
                collapsed={sidebarCollapsed}
                sectionCollapsed={!sidebarCollapsed && locationsSectionCollapsed}
                onToggleSectionCollapse={handleToggleLocationsSection}
                onLocationsLoaded={handleLocationsLoaded}
              />
            </Box>
          )}
          <BoardCalendarView
            canWrite={canWrite}
            canManage={canManage}
            onFullscreenChange={setCalendarFullscreen}
          />
        </Box>
        </DragDropContext>
      ) : viewMode === "list" ? (
        /* ─── List View ──────────────────────────────────────── */
        <>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1, overflowY: "auto", minHeight: 0 }}>
          {/* List header (desktop only) */}
          {!isPhone && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "100px 1fr 130px 120px 110px 120px",
                gap: "12px",
                padding: "8px 14px",
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 700,
                color: dt.textSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
              }}
            >
              <Box>{translate("fields.number")}</Box>
              <Box>{translate("fields.title")}</Box>
              <Box>{translate("fields.type")}</Box>
              <Box>{translate("fields.status")}</Box>
              <Box>{translate("fields.dueDate")}</Box>
              <Box>{translate("fields.assignedTo")}</Box>
            </Box>
          )}
          {filteredWorkOrders.map((wo) => {
            const pColor = priorityColors[wo.priorityCode] || priorityColors[3];
            const statusCol = COLUMNS.find(c => c.statusCodes.includes(wo.workOrderStatusId));
            const isHighlighted = changedIds.has(wo.id);
            return (
              <Box
                key={wo.id}
                onClick={() => handleCardClick(wo.id)}
                sx={{
                  display: isPhone ? "flex" : "grid",
                  flexDirection: isPhone ? "column" : undefined,
                  gridTemplateColumns: isPhone ? undefined : "100px 1fr 130px 120px 110px 120px",
                  gap: isPhone ? "6px" : "12px",
                  alignItems: isPhone ? undefined : "center",
                  padding: "12px 14px",
                  backgroundColor: dt.cardBg,
                  border: `1px solid ${dt.cardBorder}`,
                  borderLeft: `4px solid ${pColor}`,
                  borderRadius: dt.radiusSm,
                  boxShadow: dt.cardShadow,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  ...(isHighlighted && { animation: "rowHighlight 1.5s ease-out" }),
                  "&:hover": { boxShadow: dt.cardShadowHover, borderColor: dt.blue },
                }}
              >
                <Box sx={{ fontFamily: dt.fontMono, fontSize: "12px", fontWeight: 600, color: dt.textPrimary }}>
                  {wo.number}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                  <Box sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 600, color: dt.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {wo.title}
                  </Box>
                  {wo.machineName && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", color: dt.textSecondary, fontSize: "11px", fontFamily: dt.font }}>
                      <MachineIcon sx={{ fontSize: 13 }} /> {wo.machineName}
                    </Box>
                  )}
                </Box>
                <Box><WorkOrderTypeBadge typeId={wo.workOrderTypeId} typeName={wo.workOrderTypeName} /></Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: statusCol?.color || dt.textMuted, flexShrink: 0 }} />
                  <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textPrimary }}>{wo.workOrderStatusName}</Box>
                </Box>
                <Box>
                  {wo.dueDate ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "3px", fontSize: "12px", fontFamily: dt.fontMono, color: dayjs(wo.dueDate).isBefore(dayjs()) ? "#E74C3C" : dt.textSecondary }}>
                      <ScheduleIcon sx={{ fontSize: 13 }} />
                      {formatUiDate(wo.dueDate, "dayMonth")}
                    </Box>
                  ) : (
                    <Box sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font }}>—</Box>
                  )}
                </Box>
                <Box>
                  {wo.assignees && wo.assignees.length > 0 ? (
                    <AssigneeAvatarStack assignees={wo.assignees} size={22} maxVisible={3} />
                  ) : wo.assignedToUserName ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontFamily: dt.font, color: dt.textSecondary }}>
                      <PersonIcon sx={{ fontSize: 14 }} />
                      <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wo.assignedToUserName}</Box>
                    </Box>
                  ) : (
                    <Box sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font, fontStyle: "italic" }}>{translate("card.unassigned")}</Box>
                  )}
                </Box>
              </Box>
            );
          })}
          {filteredWorkOrders.length === 0 && (
            <Box sx={{ textAlign: "center", padding: "60px 0", fontFamily: dt.font, fontSize: "14px", color: dt.textSecondary }}>
              {translate("board.noWorkOrders")}
            </Box>
          )}
        </Box>
        </>
      ) : (
        /* ─── Board View ─────────────────────────────────────── */
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
          {/* Phone: Custom scrollable tab switcher with colored dots + per-column active underline */}
          {isPhone && (
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                backgroundColor: "#FFFFFF",
                borderBottom: "1px solid #E8E5DF",
                flexShrink: 0,
                marginBottom: "10px",
                borderRadius: "8px 8px 0 0",
              }}
            >
              {columnData.map((col, idx) => {
                const isActive = Math.min(activePhoneTab, columnData.length - 1) === idx;
                return (
                  <Box
                    key={col.key}
                    onClick={() => setActivePhoneTab(idx)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "10px 14px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      position: "relative",
                      transition: "all 0.15s ease",
                      /* Active tab: colored bottom border from column's own color */
                      "&::after": isActive ? {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: "8px",
                        right: "8px",
                        height: "2px",
                        backgroundColor: col.color,
                        borderRadius: "1px 1px 0 0",
                      } : {},
                    }}
                  >
                    {/* Colored dot (8px) */}
                    <Box
                      sx={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: col.color,
                        flexShrink: 0,
                      }}
                    />
                    {/* Column name (12px Sora 600) */}
                    <Box
                      sx={{
                        fontFamily: "'Sora', sans-serif",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: isActive ? "#0C1117" : "#8C9099",
                        lineHeight: "16px",
                        transition: "color 0.15s ease",
                      }}
                    >
                      {translate(col.titleKey)}
                    </Box>
                    {/* Count (10px JetBrains Mono 700, colored) */}
                    <Box
                      sx={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: "10px",
                        fontWeight: 700,
                        color: col.color,
                        lineHeight: "12px",
                      }}
                    >
                      {col.count}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* DragDropContext wraps sidebar + board for multi-type DnD */}
          <DragDropContext onDragEnd={handleDragEnd}>
            {isPhone ? (
              (() => {
                const tabIdx = Math.min(activePhoneTab, columnData.length - 1);
                const col = columnData[tabIdx];
                return col ? (
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      paddingBottom: "70px", /* MIDAS FAB clearance */
                    }}
                  >
                    <BoardColumn
                      columnKey={col.key}
                      title={translate(col.titleKey)}
                      count={col.count}
                      color={col.color}
                      tintColor={col.tintColor}
                      workOrders={col.items}
                      onCardClick={handleCardClick}
                      highlightedIds={changedIds}
                      isDragEnabled={false}
                      showQuickCreate={col.key === "backlog" && canWrite}
                      onQuickCreated={loadData}
                      isLoading={loading && filteredWorkOrders.length === 0}
                      columnIndex={tabIdx}
                    />
                  </Box>
                ) : null;
              })()
            ) : isTablet ? (
              /* ─── Tablet layout: TabletTeamsBar → KPI → horizontal scroll columns ── */
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0, flex: 1, minHeight: 0, overflow: "hidden" }}>
                {/* TabletTeamsBar inside DragDropContext for drag-to-assign */}
                {canManage && (
                  <TabletTeamsBar
                    canManage={canManage}
                    machineCount={allMachines.length}
                    locationCount={sidebarLocations.length}
                    onLocationsLoaded={handleLocationsLoaded}
                  />
                )}

                {/* Kanban columns — single scrollable region */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflow: "auto",
                    paddingBottom: "8px",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#C1C7CD transparent",
                    "&::-webkit-scrollbar": { width: "8px", height: "8px" },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#C1C7CD",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#A0A8B1",
                    },
                  }}
                >
                  {/* Inner row: grows taller than scroll container when columns have many cards */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "16px",  /* spec: column gap 16px */
                      minHeight: "100%",
                      alignItems: "flex-start",
                    }}
                  >
                    {columnData.map((col, idx) => (
                      <BoardColumn
                        key={col.key}
                        columnKey={col.key}
                        title={translate(col.titleKey)}
                        count={col.count}
                        color={col.color}
                        tintColor={col.tintColor}
                        workOrders={col.items}
                        onCardClick={handleCardClick}
                        highlightedIds={changedIds}
                        isDragEnabled={isDragEnabled}
                        showQuickCreate={col.key === "backlog" && canWrite}
                        onQuickCreated={loadData}
                        isLoading={loading && filteredWorkOrders.length === 0}
                        columnIndex={idx}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : (
              /* ─── Desktop layout: sidebar + columns side-by-side ── */
              <Box
                sx={{
                  display: "flex",
                  gap: 0,
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                {/* Sidebar: Teams + Machines stacked (desktop only, >1200px) */}
                {canManage && (
                  <Box
                    sx={{
                      width: sidebarCollapsed ? "48px" : "260px", /* spec: sidebar-width 260px */
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      flexShrink: 0,
                      transition: "width 0.25s ease",
                      animation: "sidebarSlideIn 0.3s ease both",
                      backgroundColor: "#FFFFFF", /* spec: sidebar-bg */
                      borderRight: "1px solid #E8E5DF", /* spec: border */
                      borderRadius: "12px 0 0 12px",
                      paddingTop: "16px", /* spec: sidebar padding-top */
                      paddingBottom: "16px", /* spec: sidebar padding-bottom */
                      overflowY: "auto",
                      overflowX: "hidden",
                      scrollbarWidth: "thin",
                      scrollbarColor: "#C1C7CD transparent",
                      "&::-webkit-scrollbar": { width: "8px" },
                      "&::-webkit-scrollbar-track": { background: "transparent" },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#C1C7CD",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#A0A8B1",
                      },
                    }}
                  >
                    {/* Teams section (always first) */}
                    <TeamSidebar
                      collapsed={sidebarCollapsed}
                      sectionCollapsed={!sidebarCollapsed && teamsSectionCollapsed}
                      onToggleSectionCollapse={handleToggleTeamsSection}
                      onToggleCollapse={handleToggleSidebarCollapse}
                      onOpenTeamManagement={() => setTeamDialogOpen(true)}
                      canManage={canManage}
                    />
                    {/* Divider between sections — spec: 1px #E8E5DF, 12px margin */}
                    {!sidebarCollapsed && (
                      <Box sx={{ height: "1px", backgroundColor: "#E8E5DF" /* spec: border */, margin: "12px 0", flexShrink: 0 }} />
                    )}
                    {/* Machines section (always second) */}
                    <MachineSidebar
                      collapsed={sidebarCollapsed}
                      sectionCollapsed={!sidebarCollapsed && machinesSectionCollapsed}
                      onToggleSectionCollapse={handleToggleMachinesSection}
                    />
                    {/* Divider between Machines and Locations — spec: 1px #E8E5DF, 12px margin */}
                    {!sidebarCollapsed && (
                      <Box sx={{ height: "1px", backgroundColor: "#E8E5DF" /* spec: border */, margin: "12px 0", flexShrink: 0 }} />
                    )}
                    {/* Locations section (always third) */}
                    <LocationsSidebarSection
                      collapsed={sidebarCollapsed}
                      sectionCollapsed={!sidebarCollapsed && locationsSectionCollapsed}
                      onToggleSectionCollapse={handleToggleLocationsSection}
                      onLocationsLoaded={handleLocationsLoaded}
                    />
                  </Box>
                )}

                {/* Kanban columns — single scrollable region */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflow: "auto",
                    paddingLeft: canManage ? "12px" : 0,
                    paddingBottom: "8px",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#C1C7CD transparent",
                    "&::-webkit-scrollbar": { width: "8px", height: "8px" },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#C1C7CD",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#A0A8B1",
                    },
                  }}
                >
                  {/* Inner row: grows taller than scroll container when columns have many cards */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "16px",  /* spec: column gap 16px */
                      minHeight: "100%",
                      alignItems: "flex-start",
                    }}
                  >
                    {columnData.map((col, idx) => (
                      <BoardColumn
                        key={col.key}
                        columnKey={col.key}
                        title={translate(col.titleKey)}
                        count={col.count}
                        color={col.color}
                        tintColor={col.tintColor}
                        workOrders={col.items}
                        onCardClick={handleCardClick}
                        highlightedIds={changedIds}
                        isDragEnabled={isDragEnabled}
                        showQuickCreate={col.key === "backlog" && canWrite}
                        onQuickCreated={loadData}
                        isLoading={loading && filteredWorkOrders.length === 0}
                        columnIndex={idx}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </DragDropContext>
        </Box>
      )}
      </Box>{/* end content dimming wrapper */}

      {/* Create Work Order side panel */}
      <CreateWorkOrderPanel
        open={createPanelOpen}
        onClose={() => setCreatePanelOpen(false)}
        onCreated={() => {
          setCreatePanelOpen(false);
          loadData();
        }}
        prefill={createPanelPrefill}
      />

      {/* Completion dialog */}
      <CompletionDialog
        open={!!pendingComplete}
        workOrderId={pendingComplete?.workOrderId || ""}
        workOrderNumber={pendingComplete?.workOrderNumber || ""}
        hasMachine={pendingComplete?.hasMachine || false}
        hasLinkedSRs={pendingComplete?.hasLinkedSRs || false}
        machineName={pendingComplete?.machineName}
        onConfirm={handleCompletionConfirm}
        onCancel={handleCompletionCancel}
      />

      {/* Rework dialog — manager returns WO to worker */}
      <ReworkDialog
        open={!!pendingRework}
        workOrderId={pendingRework?.workOrderId || ""}
        workOrderNumber={pendingRework?.workOrderNumber || ""}
        onConfirm={handleReworkConfirm}
        onCancel={handleReworkCancel}
      />

      {/* Stacking toast notifications — undo, error, success all share one stack */}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />

      {/* Backlog confirmation dialog */}
      <Dialog
        open={!!backlogConfirm}
        onClose={handleBacklogCancel}
        PaperProps={{
          sx: {
            borderRadius: "14px",
            padding: "8px",
            maxWidth: "400px",
            fontFamily: dt.font,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: dt.font,
            fontSize: "16px",
            fontWeight: 700,
            color: dt.textPrimary,
            paddingBottom: "4px",
          }}
        >
          {translate("backlogConfirm.title")}
        </DialogTitle>
        <DialogContent
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            color: dt.textSecondary,
            lineHeight: "20px",
          }}
        >
          {backlogConfirm
            ? translate("backlogConfirm.message", { number: backlogConfirm.workOrderNumber })
            : ""}
        </DialogContent>
        <DialogActions sx={{ padding: "8px 24px 16px", gap: "8px" }}>
          <MuiButton
            onClick={handleBacklogCancel}
            size="small"
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "none",
              color: dt.textSecondary,
            }}
          >
            {translate("backlogConfirm.cancel")}
          </MuiButton>
          <MuiButton
            onClick={handleBacklogRemoveAssignee}
            size="small"
            variant="outlined"
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "none",
              borderColor: "#E74C3C",
              color: "#E74C3C",
              "&:hover": {
                borderColor: "#C0392B",
                backgroundColor: "rgba(231, 76, 60, 0.05)",
              },
            }}
          >
            {translate("backlogConfirm.removeAssignee")}
          </MuiButton>
          <MuiButton
            onClick={handleBacklogKeepAssignee}
            size="small"
            variant="contained"
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 700,
              textTransform: "none",
              backgroundColor: dt.blue,
              "&:hover": { backgroundColor: "#2980B9" },
            }}
          >
            {translate("backlogConfirm.keepAssignee")}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Team management dialog */}
      <TeamManagementDialog
        open={teamDialogOpen}
        onClose={() => setTeamDialogOpen(false)}
      />

      {/* Location conflict dialog — shown when machine's location differs from WO's location */}
      <LocationConflictDialog
        open={!!locationConflict}
        machineName={locationConflict?.machineName || ""}
        currentLocation={locationConflict?.currentLocation || ""}
        machineLocation={locationConflict?.machineLocation || ""}
        onKeepCurrent={handleLocationConflictKeepCurrent}
        onUpdateLocation={handleLocationConflictUpdateLocation}
      />
    </Box>
  );
};

export default WorkBoardPage;
